import { useCallback, useMemo, useState } from "react"
import { Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import AppLayout from "@/components/AppLayout"
import OnThisPage, { type PageSection } from "@/components/OnThisPage"
import PageTitle from "@/components/PageTitle"
import { tasks_path } from "@/lib/routes"
import TopDownPlanningCard from "@/pages/Settings/components/TopDownPlanningCard"
import QuarterlyRevisionCard from "@/pages/Settings/components/QuarterlyRevisionCard"
import ChannelTaskCard from "@/pages/Settings/components/ChannelTaskCard"
import type { Person } from "@/lib/person"

export interface MarketSetting {
  org_node_id: string
  name: string
  start_month: number | null
  start_day: number | null
  end_month: number | null
  end_day: number | null
  days_before: number
  recipient_user_ids: string[]
  reviewer_user_ids: string[]
  skip_review: boolean
}

// Quarters are calendar quarters, so this row has no dates — only lead time
// and people.
export interface QuarterlyRevisionSetting {
  org_node_id: string
  name: string
  days_before: number
  recipient_user_ids: string[]
  reviewer_user_ids: string[]
  skip_review: boolean
}

export interface ChannelPicSetting {
  org_node_id: string
  name: string
  pic_user_ids: string[]
  reviewer_user_ids: string[]
  skip_review: boolean
}

export interface ChannelTaskMarket {
  org_node_id: string
  name: string
  due_in_days: number
  channels: ChannelPicSetting[]
}

// The page's sections, in order. The table of contents and the card anchors
// both read from this — a new section is one entry here plus its card below.
const SECTIONS = [
  { id: "top-down-planning", title: "Top Down Planning" },
  { id: "quarterly-revision", title: "Top Down Quarterly Revision" },
  { id: "products-pricings", title: "Products & Pricings" },
  { id: "monthly-plan", title: "Monthly Plan" },
] as const satisfies readonly PageSection[]

// Each card is its own form with its own Save (SettingsSection), so this
// page only lays them out.
export default function TaskSettings({ markets, quarterly_revisions, products_pricing_markets, monthly_plan_markets, people, can }: {
  markets: MarketSetting[]
  quarterly_revisions: QuarterlyRevisionSetting[]
  products_pricing_markets: ChannelTaskMarket[]
  monthly_plan_markets: ChannelTaskMarket[]
  people: Person[]
  can: { update: boolean }
}) {
  const readOnly = !can.update

  // Each card owns its form, so it reports up whether any of its tabs is
  // still unfilled; the table of contents shows a dot for those sections.
  const [unfilled, setUnfilled] = useState<Record<string, boolean>>({})
  const reportUnfilled = useCallback((id: string, value: boolean) => {
    setUnfilled((prev) => (prev[id] === value ? prev : { ...prev, [id]: value }))
  }, [])
  const sections = useMemo(() => SECTIONS.map((s) => ({ ...s, dot: unfilled[s.id] })), [unfilled])

  return (
    <AppLayout header={
      <div className="flex items-center gap-3">
        <Link
          href={tasks_path()}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          <span className="sr-only">Back to Tasks</span>
        </Link>
        <PageTitle title="Task Settings" subtitle="Settings for Tasks and Notifications" />
      </div>
    }>
      <div className="flex flex-col-reverse gap-6 lg:flex-row lg:items-start">
        <div className="flex w-full max-w-3xl flex-col gap-6">
          <TopDownPlanningCard id={SECTIONS[0].id} markets={markets} people={people} readOnly={readOnly} onUnfilledChange={reportUnfilled} />
          <QuarterlyRevisionCard id={SECTIONS[1].id} markets={quarterly_revisions} people={people} readOnly={readOnly} onUnfilledChange={reportUnfilled} />
          <ChannelTaskCard id={SECTIONS[2].id} title="Products & Pricings" field="products_pricing" markets={products_pricing_markets} people={people} readOnly={readOnly} onUnfilledChange={reportUnfilled} />
          <ChannelTaskCard id={SECTIONS[3].id} title="Monthly Plan" field="monthly_plan" markets={monthly_plan_markets} people={people} readOnly={readOnly} onUnfilledChange={reportUnfilled} />
        </div>
        <OnThisPage sections={sections} className="w-full lg:sticky lg:top-0 lg:w-56" />
      </div>
    </AppLayout>
  )
}
